import React, { useEffect, useState } from 'react';
import { Button, Modal } from '../../../shared/components';
import { notificationsService } from '../../../shared/services/notificationsService';
import { getNotificationTypeLabel } from './notificationTypes';
import MessagePreview from './MessagePreview';

const Count = ({ label, value }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
    <div className="text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
    <div className="text-xs text-gray-600">{label}</div>
  </div>
);

/**
 * Reenvía por la plantilla informativa los avisos que Meta bloqueó por ser
 * marketing (130472 / 131049) y cancela los originales fallidos.
 */
const MarketingBlockedResendModal = ({ isOpen, onClose, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    let active = true;
    setPreview(null);
    setViewing(null);
    setError(null);
    setLoading(true);
    notificationsService
      .previewMarketingBlockedResend()
      .then((data) => active && setPreview(data))
      .catch((err) => active && setError(err.message))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [isOpen]);

  const canConfirm = !!preview && (preview.notices > 0 || preview.skipped > 0) && !submitting;

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await notificationsService.resendMarketingBlocked();
      await onDone(
        `Reenvío encolado: ${result.notices} aviso(s) a ${result.phones} teléfono(s) y ${result.codes} código(s). ` +
          `${result.resolved} mensaje(s) originales marcados como reenviados.`
      );
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Texto exacto que saldría, en lugar de la lista, con vuelta atrás
  if (isOpen && viewing) {
    return (
      <Modal isOpen onClose={onClose} title="Mensaje que se enviaría" size="lg">
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            {getNotificationTypeLabel(viewing.fallbackType)} ·{' '}
            <span className="font-mono">{viewing.recipientPhone}</span>
          </div>
          <MessagePreview message={viewing.message} />
          {viewing.codeMessages.map((text, index) => (
            <MessagePreview
              key={viewing.codes[index]}
              title={`A continuación: ${getNotificationTypeLabel('access_code_delivery')}`}
              message={text}
            />
          ))}
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setViewing(null)}>
              Volver
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Reenviar bloqueados por Meta" size="lg">
      <div className="space-y-4">
        <p className="text-sm text-gray-700">
          Meta bloqueó el aviso por ser marketing; se reenvía con la plantilla informativa que
          corresponde a cada tipo de mensaje (en el inicio de venta, junto con los códigos de
          acceso que no llegaron). Los mensajes originales fallidos pasan a Reenviado (conservan
          el error de Meta).
        </p>

        {loading && <p className="text-sm text-gray-500">Calculando…</p>}

        {preview && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Count label="Avisos a reenviar" value={preview.notices} />
              <Count label="Teléfonos" value={preview.phones} />
              <Count label="Códigos" value={preview.codes} />
              <Count label="Ya reenviados" value={preview.skipped} />
            </div>

            {preview.byType.length > 0 && (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-xs text-left">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Tipo</th>
                      <th className="px-3 py-2 font-semibold">Se reenvía como</th>
                      <th className="px-3 py-2 font-semibold text-right">Avisos</th>
                      <th className="px-3 py-2 font-semibold text-right">Teléfonos</th>
                      <th className="px-3 py-2 font-semibold text-right">Códigos</th>
                      <th className="px-3 py-2 font-semibold text-right">Ya reenviados</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-gray-800">
                    {preview.byType.map((t) => (
                      <tr key={t.type}>
                        <td className="px-3 py-2">{getNotificationTypeLabel(t.type)}</td>
                        <td className="px-3 py-2">{getNotificationTypeLabel(t.fallbackType)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{t.notices}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{t.phones}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{t.codes}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{t.skipped}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {preview.withoutFallback.length > 0 && (
              <ul className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg divide-y divide-amber-100">
                {preview.withoutFallback.map((w) => (
                  <li key={w.type} className="px-3 py-2">
                    <span className="font-semibold">{getNotificationTypeLabel(w.type)}</span>:{' '}
                    {w.notices} aviso(s) a {w.phones} teléfono(s). Sin plantilla alternativa: no se
                    reenvía.
                  </li>
                ))}
              </ul>
            )}

            {preview.samples.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1">
                  Ejemplos ({preview.samples.length} de {preview.notices})
                </h3>
                <ul className="text-xs text-gray-700 divide-y divide-gray-100 border border-gray-200 rounded-lg">
                  {preview.samples.map((s) => (
                    <li
                      key={`${s.recipientPhone}-${s.message}`}
                      className="px-3 py-2 flex items-center justify-between gap-2"
                    >
                      <span>
                        <span className="font-mono">{s.recipientPhone}</span> ·{' '}
                        {getNotificationTypeLabel(s.type)}
                        {s.codes.length > 0 && <span> · códigos: {s.codes.join(', ')}</span>}
                      </span>
                      <Button variant="outline" size="sm" onClick={() => setViewing(s)}>
                        Ver
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {preview.notices === 0 && (
              <p className="text-sm text-gray-600">
                {preview.skipped > 0
                  ? 'No hay nada nuevo para enviar. Al confirmar se marcan como reenviados los avisos que ya se habían reenviado.'
                  : preview.withoutFallback.length > 0
                  ? 'No hay mensajes con plantilla alternativa para reenviar.'
                  : 'No hay mensajes bloqueados por Meta para reenviar.'}
              </p>
            )}
          </>
        )}

        {error && <p className="text-sm text-red-700">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm}>
            {submitting ? 'Reenviando...' : 'Confirmar reenvío'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MarketingBlockedResendModal;
